/**
 * Сессия веба: access-токен в памяти, обновление по refresh-cookie (заранее по таймеру и по 401),
 * выбранная клиника (ПОРТ-16), второй фактор (ПОРТ-21), блокировка после бездействия (ПОРТ-22),
 * синхронизация выхода и блокировки между вкладками. Без React — тестируется отдельно.
 *
 * Блокировка бывает двух видов:
 * - клиентская: 30 минут без действий во вкладке (таймер бездействия) — экран закрывается, обновление
 *   токена останавливается; если блокировка длится дольше жизни токена, сервер тоже блокирует сессию;
 * - серверная: refresh отвечает 423 IDENTITY_SESSION_LOCKED (вкладка спала, ноутбук был закрыт).
 * Разблокировка — PIN устройства или код из SMS/WhatsApp; сервер выдаёт новые токены.
 */
import { ApiError } from '@dodoc/api-client';
import { readStorage, STORAGE_KEYS, writeStorage } from '@/shared/lib/storage';
import { DEFAULT_IDLE_MINUTES, SESSION_LOCKED_CODE, type AuthApi } from './authApi';
import type { AccessToken, AuthResult, LockPolicy, MfaProof, OtpChallenge, OtpChannel, SessionMembership, SessionUser, TotpSetup } from './types';

/**
 * loading — проверяем сессию; anonymous — не вошли; mfa_required — нужен второй фактор;
 * locked — сессия заблокирована сервером ещё до загрузки пользователя; authenticated — работаем.
 */
export type SessionStatus = 'loading' | 'anonymous' | 'mfa_required' | 'locked' | 'authenticated';
/** Почему сессия закончилась: для понятного сообщения на экране входа. */
export type SessionEndReason = 'expired' | 'revoked' | 'logout' | 'other_tab';

export interface SessionState {
  status: SessionStatus;
  user: SessionUser | null;
  accessToken: string | null;
  accessExpiresAt: number | null;
  clinicId: string | null;
  locked: boolean;
  lockPolicy: LockPolicy;
  endReason: SessionEndReason | null;
  /** Не удалось проверить сессию при запуске (нет сети, 5xx) — показывается ошибка с повтором. */
  bootstrapError: ApiError | null;
}

export interface SessionStoreOptions {
  authApi: AuthApi;
  now?: () => number;
  /** За сколько до истечения обновлять токен (по умолчанию 60 с). */
  refreshLeewayMs?: number;
  /** Смена клиники или конец сессии: очистить кэш данных, переподключить события (данные клиник не смешиваются). */
  onContextReset?: (reason: 'clinic' | 'session') => void;
  /** Синхронизация выхода и блокировки между вкладками через storage-события и Web Locks. */
  crossTab?: boolean;
}

/**
 * Политика до ответа сервера. Признак PIN помнится между перезагрузками: если сессия заблокирована
 * сервером при запуске (423), сервер не сообщает, задан ли PIN, а предложить PIN первым удобнее.
 */
function defaultLockPolicy(): LockPolicy {
  return { idleMinutes: DEFAULT_IDLE_MINUTES, pinEnabled: readStorage(STORAGE_KEYS.pinEnabled) === '1' };
}

function initialState(): SessionState {
  return {
    status: 'loading',
    user: null,
    accessToken: null,
    accessExpiresAt: null,
    clinicId: null,
    locked: false,
    lockPolicy: defaultLockPolicy(),
    endReason: null,
    bootstrapError: null,
  };
}

/** Ответы refresh, после которых сессии больше нет (повтор старого токена, отзыв, удалённое устройство). */
const REVOKING_CODES = new Set(['SESSION_REVOKED', 'REFRESH_TOKEN_INVALID', 'IDENTITY_DEVICE_WIPE_REQUIRED', 'IDENTITY_USER_BLOCKED']);
/** Имя Web Lock: обновление токена во всех вкладках по очереди (иначе ротация refresh-токена сочтёт повтор кражей). */
const REFRESH_LOCK = 'dodoc:auth-refresh';

function unauthorized(): ApiError {
  return new ApiError(401, { error: { code: 'UNAUTHORIZED', message: 'No session', params: {}, requestId: null } });
}

export class SessionStore {
  private state: SessionState = initialState();
  private readonly listeners = new Set<() => void>();
  private refreshPromise: Promise<string | null> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly now: () => number;
  private readonly leeway: number;
  private readonly storageListener = (event: StorageEvent) => this.handleStorage(event);

  constructor(private readonly options: SessionStoreOptions) {
    this.now = options.now ?? (() => Date.now());
    this.leeway = options.refreshLeewayMs ?? 60_000;
    if (options.crossTab && typeof window !== 'undefined') window.addEventListener('storage', this.storageListener);
  }

  getState = (): SessionState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private setState(patch: Partial<SessionState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** Проверка сессии при запуске: refresh-cookie → access-токен → /me и /me/clinics. */
  async bootstrap(): Promise<void> {
    this.setState({ status: 'loading', bootstrapError: null });
    try {
      const token = await this.refreshAccessToken();
      if (!token) {
        if (this.state.status === 'loading') this.setState({ status: 'anonymous' });
        return;
      }
      await this.loadUser();
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        this.endSession('expired');
        return;
      }
      this.setState({ status: 'loading', bootstrapError: error instanceof ApiError ? error : new ApiError(0, null) });
    }
  }

  /**
   * Обновить access-токен (один запрос на все одновременные 401 и одна вкладка за раз).
   * null — токена нет: сессия закончилась или заблокирована (тогда locked = true).
   */
  refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = (async () => {
      try {
        const result = await this.withRefreshLock(() => this.options.authApi.refresh());
        if (!result) {
          if (this.state.accessToken || this.state.user) this.endSession('expired');
          return null;
        }
        this.applyAuthResult(result);
        return result.token.accessToken;
      } catch (error) {
        if (error instanceof ApiError && error.code === SESSION_LOCKED_CODE) {
          this.markLocked();
          return null;
        }
        if (error instanceof ApiError && REVOKING_CODES.has(error.code)) {
          this.endSession('revoked');
          return null;
        }
        throw error;
      } finally {
        this.refreshPromise = null;
      }
    })();
    return this.refreshPromise;
  }

  /** Текущий токен, при необходимости обновлённый (для EventSource и ручных запросов). */
  async ensureFreshToken(): Promise<string | null> {
    const { accessToken, accessExpiresAt, locked } = this.state;
    if (accessToken && accessExpiresAt && accessExpiresAt - this.now() > this.leeway) return accessToken;
    if (locked) return null;
    return this.refreshAccessToken();
  }

  /** Вход завершён экраном входа: сервер выдал токены (AuthResultDto). */
  async signIn(result: AuthResult): Promise<void> {
    this.applyAuthResult(result);
    this.setState({ endReason: null, locked: false });
    await this.loadUser();
  }

  /** Сервер ответил MFA_REQUIRED: нужен второй фактор (ПОРТ-21). */
  markMfaRequired(): void {
    if (this.state.status === 'authenticated') this.setState({ status: 'mfa_required' });
  }

  /** Второй фактор: код из приложения или код восстановления → новый токен с mfa=true. */
  async verifyMfa(proof: MfaProof): Promise<void> {
    const next = await this.options.authApi.verifyMfa(proof, this.requireToken());
    this.applyToken(next);
    await this.loadUser();
  }

  /** Подключение приложения-аутентификатора (MFA обязательна для роли, но ещё не подключена). */
  setupTotp(): Promise<TotpSetup> {
    return this.options.authApi.setupTotp(this.requireToken());
  }

  /** Включить TOTP первым кодом. Коды восстановления показываются один раз; вход завершает completeMfa(). */
  async enableTotp(code: string): Promise<string[]> {
    const result = await this.options.authApi.enableTotp(code, this.requireToken());
    this.applyToken(result.token);
    return result.recoveryCodes;
  }

  /** После показа кодов восстановления: перечитать пользователя (mfa.passed) и открыть портал. */
  completeMfa(): Promise<void> {
    return this.loadUser();
  }

  /**
   * Перечитать пользователя и его клиники после изменения членств: регистрация клиники (ПОРТ-10),
   * принятие приглашения (ПОРТ-13), личное пространство врача (ПОРТ-15). Новая роль может
   * потребовать второй фактор (ПОРТ-21) — тогда статус станет mfa_required.
   */
  reloadUser(): Promise<void> {
    return this.loadUser();
  }

  /** Переключатель клиники (ПОРТ-16): кэш данных прежней клиники очищается, выбор запоминается на сервере. */
  switchClinic(clinicId: string): void {
    const { user, accessToken } = this.state;
    if (!user || !user.memberships.some((membership) => membership.clinicId === clinicId)) return;
    if (this.state.clinicId === clinicId) return;
    writeStorage(STORAGE_KEYS.lastClinic(user.id), clinicId);
    this.setState({ clinicId, user: { ...user, lastClinicId: clinicId } });
    this.options.onContextReset?.('clinic');
    if (accessToken) {
      this.options.authApi.rememberClinic(clinicId, accessToken).catch(() => {
        /* выбор уже применён во вкладке и сохранён локально; сервер запомнит при следующей смене */
      });
    }
  }

  currentMembership(): SessionMembership | null {
    const { user, clinicId } = this.state;
    return user?.memberships.find((membership) => membership.clinicId === clinicId) ?? null;
  }

  /** Блокировка после бездействия (ПОРТ-22): экран закрыт, обновление токена остановлено. */
  lock(): void {
    if (this.state.status !== 'authenticated' || this.state.locked) return;
    this.markLocked();
    if (this.options.crossTab) writeStorage(STORAGE_KEYS.sessionLocked, '1');
  }

  /** Код разблокировки на номер владельца сессии. */
  requestUnlockCode(channel: OtpChannel): Promise<OtpChallenge> {
    return this.options.authApi.requestUnlockCode(channel);
  }

  async unlockWithPin(pin: string): Promise<void> {
    await this.completeUnlock(await this.withRefreshLock(() => this.options.authApi.unlockWithPin(pin)));
  }

  async unlockWithCode(challengeId: string, code: string): Promise<void> {
    await this.completeUnlock(await this.withRefreshLock(() => this.options.authApi.unlockWithCode(challengeId, code)));
  }

  async signOut(): Promise<void> {
    const token = this.state.accessToken;
    try {
      await this.options.authApi.logout(token);
    } catch {
      /* сервер недоступен или токен истёк — локальная сессия всё равно завершается */
    }
    this.endSession('logout');
  }

  /** Завершить сессию локально: токен, пользователь, кэш данных и таймеры очищаются. */
  endSession(reason: SessionEndReason): void {
    this.clearRefreshTimer();
    const hadSession = this.state.accessToken !== null || this.state.user !== null;
    this.setState({ ...initialState(), status: 'anonymous', endReason: reason });
    if (hadSession) this.options.onContextReset?.('session');
    if (this.options.crossTab && reason !== 'other_tab') writeStorage(STORAGE_KEYS.sessionEnded, String(this.now()));
  }

  dispose(): void {
    this.clearRefreshTimer();
    if (typeof window !== 'undefined') window.removeEventListener('storage', this.storageListener);
    this.listeners.clear();
  }

  private requireToken(): string {
    const token = this.state.accessToken;
    if (!token) throw unauthorized();
    return token;
  }

  private markLocked(): void {
    this.clearRefreshTimer();
    this.setState({ locked: true, status: this.state.user ? this.state.status : 'locked' });
  }

  private async completeUnlock(result: AuthResult): Promise<void> {
    this.applyAuthResult(result);
    if (!this.state.user) await this.loadUser();
    this.setState({ locked: false });
    if (this.options.crossTab) writeStorage(STORAGE_KEYS.sessionLocked, '0');
  }

  private applyAuthResult(result: AuthResult): void {
    if (result.lockPolicy) {
      this.setState({ lockPolicy: result.lockPolicy });
      writeStorage(STORAGE_KEYS.pinEnabled, result.lockPolicy.pinEnabled ? '1' : '0');
    }
    this.applyToken(result.token);
  }

  private applyToken(token: AccessToken): void {
    const expiresAt = Date.parse(token.accessExpiresAt);
    this.setState({ accessToken: token.accessToken, accessExpiresAt: Number.isNaN(expiresAt) ? null : expiresAt });
    this.scheduleRefresh();
  }

  private async loadUser(): Promise<void> {
    const user = await this.options.authApi.me(this.requireToken());
    const stored = readStorage(STORAGE_KEYS.lastClinic(user.id));
    const pick =
      [this.state.clinicId, user.lastClinicId, stored].find(
        (candidate) => candidate && user.memberships.some((membership) => membership.clinicId === candidate),
      ) ??
      user.memberships[0]?.clinicId ??
      null;
    const mfaPending = user.mfa.required && !user.mfa.passed;
    this.setState({ user, clinicId: pick, status: mfaPending ? 'mfa_required' : 'authenticated', bootstrapError: null });
  }

  /** Обновление и разблокировка по очереди между вкладками (Web Locks), где браузер это поддерживает. */
  private withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (!this.options.crossTab || !locks) return task();
    return locks.request(REFRESH_LOCK, () => task()) as Promise<T>;
  }

  private scheduleRefresh(): void {
    this.clearRefreshTimer();
    const { accessExpiresAt } = this.state;
    if (!accessExpiresAt) return;
    const delay = Math.max(5_000, accessExpiresAt - this.now() - this.leeway);
    this.refreshTimer = setTimeout(() => {
      if (this.state.locked) return;
      this.refreshAccessToken().catch(() => {
        /* сеть недоступна — следующий запрос повторит обновление по 401 */
      });
    }, delay);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private handleStorage(event: StorageEvent): void {
    if (event.key === STORAGE_KEYS.sessionEnded && event.newValue && (this.state.accessToken || this.state.user)) {
      this.endSession('other_tab');
    } else if (event.key === STORAGE_KEYS.sessionLocked) {
      if (event.newValue === '1' && this.state.status === 'authenticated' && !this.state.locked) this.markLocked();
      if (event.newValue === '0' && this.state.locked) {
        /* Другая вкладка разблокировала сессию: cookie уже обновлена — берём свежий токен. */
        this.refreshAccessToken()
          .then((token) => {
            if (token) this.setState({ locked: false, status: this.state.user ? this.state.status : 'loading' });
            if (token && !this.state.user) return this.loadUser();
            return undefined;
          })
          .catch(() => {
            /* нет сети — вкладка останется заблокированной до ввода PIN или кода */
          });
      }
    }
  }
}
